/**
 * Parking — EV charger detail (/parking/chargers/:id) — V0.3 marquee Tier 1.
 *
 * Three-tab surface:
 *   Sessions      — last 90 days of EV sessions (paginated).
 *   Maintenance   — maintenance log entries.
 *   Revenue       — kWh + ₹ + session count rollup.
 *
 * Backend contracts:
 *   GET   /v1/parking/chargers/:id                                 (single)
 *   GET   /v1/parking/ev-sessions?charger_id=:id&cursor&limit
 *   POST  /v1/parking/ev-sessions/start                            (manual start)
 *   POST  /v1/parking/ev-sessions/:id/end                          body { kwh_consumed }
 *   POST  /v1/parking/ev-sessions/:id/mark-paid                    body { payment_ref }
 *   GET   /v1/parking/chargers/:id/photo                           (stream)
 *
 * Maintenance log endpoint not contracted yet — render an empty stub so the
 * tab is visible without coupling to a non-existent route.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  ArrowLeft,
  Zap,
  ImageIcon,
  Plug,
  IndianRupee,
  AlertTriangle,
  Wrench,
  Play,
  StopCircle,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import { get, post, getErrorMessage, api } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TabsPills } from '@/components/ui/tabs-pills';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { CursorPaginator } from '@/components/ui/cursor-paginator';
import { useCursorAccumulator } from '@/hooks/useCursorAccumulator';
import { formatDateTime } from '@/lib/format';
import type { EvCharger, EvSession, UnitLite, CursorList } from './_parking-types';
import { CHARGER_STATUS_LABEL, CHARGER_STATUS_BADGE } from './_parking-types';

function formatInr(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `₹${n.toLocaleString('en-IN')}`;
}

type Tab = 'sessions' | 'maintenance' | 'revenue';

const StartSessionSchema = z.object({
  using_unit_id: z.string().optional().or(z.literal('')),
  using_user_name: z.string().trim().max(120).optional().or(z.literal('')),
  notes: z.string().trim().max(500).optional().or(z.literal(''))
});
type StartSessionForm = z.infer<typeof StartSessionSchema>;

const EndSessionSchema = z.object({
  kwh_consumed: z.coerce.number().min(0).max(10000)
});
type EndSessionForm = z.infer<typeof EndSessionSchema>;

const MarkPaidSchema = z.object({
  payment_ref: z.string().trim().min(1, 'Payment reference required').max(120)
});
type MarkPaidForm = z.infer<typeof MarkPaidSchema>;

export function ParkingChargerDetailPage(): JSX.Element {
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('sessions');

  const chargerQ = useQuery({
    queryKey: ['parking-chargers', 'detail', id],
    queryFn: () => get<EvCharger>(`/parking/chargers/${id}`),
    enabled: Boolean(id)
  });
  const charger = chargerQ.data;

  // Sessions list (cursor-paginated).
  const filtersHash = useMemo(() => JSON.stringify([id]), [id]);
  const [cursor, setCursor] = useState<string | null>(null);
  useEffect(() => {
    setCursor(null);
  }, [filtersHash]);

  const sessionsQ = useQuery({
    queryKey: ['parking-ev-sessions', id, cursor],
    queryFn: () =>
      get<CursorList<EvSession>>('/parking/ev-sessions', {
        params: { charger_id: id, limit: 50, ...(cursor ? { cursor } : {}) }
      }),
    enabled: Boolean(id) && tab === 'sessions'
  });
  const sessionsPaginator = useCursorAccumulator<EvSession>(sessionsQ.data, cursor, filtersHash);
  const sessions = sessionsPaginator.items;

  // Photo preview (auth-gated blob).
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!charger?.photo_ref) {
      setPreviewUrl(null);
      return;
    }
    let revoked = false;
    let blobUrl: string | null = null;
    api
      .get<Blob>(`/parking/chargers/${id}/photo`, { responseType: 'blob' })
      .then((res) => {
        if (revoked) return;
        blobUrl = URL.createObjectURL(res.data);
        setPreviewUrl(blobUrl);
      })
      .catch(() => {
        if (!revoked) setPreviewUrl(null);
      });
    return () => {
      revoked = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [charger?.photo_ref, id]);

  // Units list for the manual-start picker.
  const unitsQ = useQuery({
    queryKey: ['units-lite'],
    queryFn: () => get<{ items: UnitLite[] }>('/society/units', { params: { limit: 1000 } }),
    staleTime: 5 * 60 * 1000
  });

  // Start-session dialog
  const [startOpen, setStartOpen] = useState(false);
  const startForm = useForm<StartSessionForm>({
    resolver: zodResolver(StartSessionSchema),
    mode: 'onChange',
    defaultValues: { using_unit_id: '', using_user_name: '', notes: '' }
  });
  const startSession = useMutation({
    mutationFn: (v: StartSessionForm) =>
      post<EvSession>('/parking/ev-sessions/start', {
        charger_id: id,
        using_unit_id: v.using_unit_id || null,
        using_user_name: v.using_user_name?.trim() || null,
        notes: v.notes?.trim() || null
      }),
    onSuccess: () => {
      toast.success('Session started');
      setStartOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['parking-ev-sessions', id] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // End-session dialog
  const [endTarget, setEndTarget] = useState<EvSession | null>(null);
  const endForm = useForm<EndSessionForm>({
    resolver: zodResolver(EndSessionSchema),
    mode: 'onChange',
    defaultValues: { kwh_consumed: 0 }
  });
  useEffect(() => {
    if (endTarget) endForm.reset({ kwh_consumed: 0 });
  }, [endTarget]); // eslint-disable-line react-hooks/exhaustive-deps
  const endSession = useMutation({
    mutationFn: (v: EndSessionForm) => {
      if (!endTarget) throw new Error('No session');
      return post<EvSession>(`/parking/ev-sessions/${endTarget.id}/end`, {
        kwh_consumed: v.kwh_consumed
      });
    },
    onSuccess: () => {
      toast.success('Session ended');
      setEndTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['parking-ev-sessions', id] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // Mark-paid dialog
  const [payTarget, setPayTarget] = useState<EvSession | null>(null);
  const payForm = useForm<MarkPaidForm>({
    resolver: zodResolver(MarkPaidSchema),
    mode: 'onChange',
    defaultValues: { payment_ref: '' }
  });
  useEffect(() => {
    if (payTarget) payForm.reset({ payment_ref: '' });
  }, [payTarget]); // eslint-disable-line react-hooks/exhaustive-deps
  const markPaid = useMutation({
    mutationFn: (v: MarkPaidForm) => {
      if (!payTarget) throw new Error('No session');
      return post<EvSession>(`/parking/ev-sessions/${payTarget.id}/mark-paid`, {
        payment_ref: v.payment_ref
      });
    },
    onSuccess: () => {
      toast.success('Marked paid');
      setPayTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['parking-ev-sessions', id] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  if (chargerQ.isLoading) {
    return (
      <>
        <PageHeader title="Charger" description="Loading…" />
        <div className="mx-auto w-full max-w-[1200px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
          <Card>
            <CardContent>
              <Skeleton className="h-40 w-full" />
            </CardContent>
          </Card>
        </div>
      </>
    );
  }
  if (chargerQ.isError || !charger) {
    return (
      <>
        <PageHeader title="Charger" description="Could not load this charger." />
        <div className="mx-auto w-full max-w-[1200px] px-vt-6 pb-vt-8 pt-vt-4">
          <Card>
            <CardContent>
              <EmptyState
                icon={<AlertTriangle className="h-6 w-6" />}
                title="Charger not found"
                description={chargerQ.error ? getErrorMessage(chargerQ.error) : 'This record may have been removed.'}
                action={
                  <Button variant="secondary" onClick={() => void navigate({ to: '/parking/chargers' })}>
                    <ArrowLeft className="h-4 w-4" /> Back to chargers
                  </Button>
                }
              />
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  // Revenue rollup (also returned by server in charger.total_*; compute locally
  // as a safety net so the tab is functional even if those fields are absent).
  const localKwh = sessions.reduce((s, x) => s + (x.kwh_consumed ?? 0), 0);
  const localRevenue = sessions.reduce((s, x) => s + (x.amount_inr ?? 0), 0);

  return (
    <>
      <PageHeader
        title={charger.location_label ?? 'EV charger'}
        description={`${charger.brand ?? ''}${charger.brand && charger.model ? ' ' : ''}${charger.model ?? ''} · ${charger.power_kw} kW`}
        actions={
          <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/parking/chargers' })} title="Back">
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
        }
      />

      <div className="mx-auto w-full max-w-[1200px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
        {/* Header card */}
        <Card>
          <CardContent className="flex flex-col gap-vt-4 p-vt-5 md:flex-row md:items-start">
            <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-vt-md border border-vt-border bg-vt-surface-2">
              {previewUrl ? (
                <img src={previewUrl} alt="Charger" className="h-full w-full object-cover" />
              ) : (
                <ImageIcon className="h-10 w-10 text-vt-text-soft" strokeWidth={1.5} />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-vt-2">
                <h2 className="truncate text-vt-24 font-semibold tracking-[-0.01em] text-vt-text">
                  {charger.location_label ?? 'Untitled charger'}
                </h2>
                <Badge variant={CHARGER_STATUS_BADGE[charger.status]}>
                  {CHARGER_STATUS_LABEL[charger.status]}
                </Badge>
              </div>
              <div className="mt-vt-1 flex flex-wrap items-center gap-vt-3 text-vt-13 text-vt-text-muted">
                <span className="inline-flex items-center gap-1">
                  <Zap className="h-3.5 w-3.5" />
                  {charger.power_kw} kW
                </span>
                <span className="inline-flex items-center gap-1">
                  <Plug className="h-3.5 w-3.5" />
                  {charger.connector_type}
                </span>
                <span>·</span>
                <span>
                  {charger.ownership_kind === 'society_installed' ? 'Society-installed' : 'Owner-installed'}
                </span>
                {charger.kwh_rate_inr ? (
                  <span className="inline-flex items-center gap-1">
                    <IndianRupee className="h-3.5 w-3.5" />
                    {charger.kwh_rate_inr}/kWh
                  </span>
                ) : null}
              </div>
              {charger.notes ? (
                <p className="mt-vt-2 whitespace-pre-wrap text-vt-13 text-vt-text-muted">
                  {charger.notes}
                </p>
              ) : null}
            </div>
            <div className="shrink-0">
              <Button size="sm" onClick={() => setStartOpen(true)} disabled={charger.status !== 'active'}>
                <Play className="h-4 w-4" /> Start session
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Card>
          <CardContent className="flex items-center gap-vt-2 p-vt-3">
            <TabsPills
              value={tab}
              onChange={(v) => setTab(v as Tab)}
              options={[
                { value: 'sessions', label: 'Sessions' },
                { value: 'maintenance', label: 'Maintenance' },
                { value: 'revenue', label: 'Revenue' }
              ]}
              aria-label="Charger sections"
            />
          </CardContent>
        </Card>

        {tab === 'sessions' ? (
          <Card>
            <CardContent className="p-vt-4">
              {sessionsQ.isLoading ? (
                <div className="flex flex-col gap-vt-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : sessions.length === 0 ? (
                <EmptyState
                  icon={<Zap className="h-6 w-6" />}
                  title="No sessions yet"
                  description="Start a session manually or wait for the next charge event."
                  action={
                    charger.status === 'active' ? (
                      <Button onClick={() => setStartOpen(true)}>
                        <Play className="h-4 w-4" /> Start session
                      </Button>
                    ) : null
                  }
                />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Started</TableHead>
                      <TableHead>Ended</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead className="text-right">kWh</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sessions.map((s) => (
                      <TableRow key={s.id}>
                        <TableCell className="whitespace-nowrap text-vt-12 text-vt-text-muted">
                          {formatDateTime(s.started_at)}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-vt-12 text-vt-text-muted">
                          {s.ended_at ? formatDateTime(s.ended_at) : '—'}
                        </TableCell>
                        <TableCell className="text-vt-13 text-vt-text-muted">
                          {s.using_unit?.number ?? '—'}
                        </TableCell>
                        <TableCell className="text-right font-mono text-vt-13 text-vt-text">
                          {s.kwh_consumed ?? '—'}
                        </TableCell>
                        <TableCell className="text-right font-mono text-vt-13 text-vt-text">
                          {formatInr(s.amount_inr)}
                        </TableCell>
                        <TableCell>
                          {s.status === 'active' ? (
                            <Badge variant="info">Active</Badge>
                          ) : s.status === 'paid' ? (
                            <Badge variant="ok">Paid</Badge>
                          ) : (
                            <Badge variant="muted">Ended</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="inline-flex items-center gap-vt-1">
                            {s.status === 'active' ? (
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => setEndTarget(s)}
                              >
                                <StopCircle className="h-4 w-4" /> End
                              </Button>
                            ) : null}
                            {s.status === 'ended' ? (
                              <Button size="sm" onClick={() => setPayTarget(s)}>
                                <CheckCircle2 className="h-4 w-4" /> Mark paid
                              </Button>
                            ) : null}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              <CursorPaginator
                hasMore={Boolean(sessionsPaginator.nextCursor)}
                loading={sessionsQ.isFetching}
                itemsShown={sessions.length}
                pageSize={50}
                onLoadMore={() => {
                  if (sessionsPaginator.nextCursor) setCursor(sessionsPaginator.nextCursor);
                }}
              />
            </CardContent>
          </Card>
        ) : null}

        {tab === 'maintenance' ? (
          <Card>
            <CardContent>
              <EmptyState
                icon={<Wrench className="h-6 w-6" />}
                title="No maintenance entries"
                description="Log entries will appear here once the maintenance API ships."
              />
            </CardContent>
          </Card>
        ) : null}

        {tab === 'revenue' ? (
          <div className="grid grid-cols-1 gap-vt-4 md:grid-cols-3">
            <Card>
              <CardContent className="p-vt-5">
                <div className="text-vt-12 uppercase tracking-wide text-vt-text-soft">Total sessions</div>
                <div className="mt-vt-1 text-vt-24 font-semibold text-vt-text">
                  {charger.total_sessions ?? sessions.length}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-vt-5">
                <div className="text-vt-12 uppercase tracking-wide text-vt-text-soft">Total kWh</div>
                <div className="mt-vt-1 text-vt-24 font-semibold text-vt-text">
                  {(charger.total_kwh ?? localKwh).toFixed(2)}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-vt-5">
                <div className="text-vt-12 uppercase tracking-wide text-vt-text-soft">Total revenue</div>
                <div className="mt-vt-1 text-vt-24 font-semibold text-vt-text">
                  {formatInr(charger.total_revenue_inr ?? localRevenue)}
                </div>
              </CardContent>
            </Card>
          </div>
        ) : null}
      </div>

      {/* Start session */}
      <Dialog open={startOpen} onOpenChange={setStartOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start EV session</DialogTitle>
            <DialogDescription>
              Pick the using unit (optional — leave blank for walk-in / guest).
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={startForm.handleSubmit((v) => startSession.mutate(v))}
            className="flex flex-col gap-vt-3"
          >
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="ss-unit">Using unit</Label>
              <Select id="ss-unit" {...startForm.register('using_unit_id')}>
                <option value="">— None / guest —</option>
                {(unitsQ.data?.items ?? []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.block ? `${u.block.name} · ` : ''}
                    {u.number}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="ss-user">User name (optional)</Label>
              <Input id="ss-user" placeholder="Walk-in guest" {...startForm.register('using_user_name')} />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setStartOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={startSession.isPending}
                loadingText="Starting…"
                disabled={startSession.isPending}
              >
                <Play className="h-4 w-4" /> Start
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* End session */}
      <Dialog open={Boolean(endTarget)} onOpenChange={(v) => (v ? null : setEndTarget(null))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>End session</DialogTitle>
            <DialogDescription>
              Enter the kWh consumed. The amount is calculated server-side from the rate.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={endForm.handleSubmit((v) => endSession.mutate(v))}
            className="flex flex-col gap-vt-3"
          >
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="es-kwh">kWh consumed</Label>
              <Input id="es-kwh" type="number" min={0} step="0.01" {...endForm.register('kwh_consumed')} />
              {endForm.formState.errors.kwh_consumed ? (
                <p className="text-vt-12 text-vt-alert">{endForm.formState.errors.kwh_consumed.message}</p>
              ) : null}
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setEndTarget(null)}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="secondary"
                loading={endSession.isPending}
                loadingText="Ending…"
                disabled={endSession.isPending || !endForm.formState.isValid}
              >
                End session
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Mark paid */}
      <Dialog open={Boolean(payTarget)} onOpenChange={(v) => (v ? null : setPayTarget(null))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark session paid</DialogTitle>
            <DialogDescription>
              {payTarget ? `Amount due: ${formatInr(payTarget.amount_inr)}` : ''}
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={payForm.handleSubmit((v) => markPaid.mutate(v))}
            className="flex flex-col gap-vt-3"
          >
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="mp-ref">Payment reference</Label>
              <Input id="mp-ref" placeholder="UTR / cheque #" {...payForm.register('payment_ref')} />
              {payForm.formState.errors.payment_ref ? (
                <p className="text-vt-12 text-vt-alert">{payForm.formState.errors.payment_ref.message}</p>
              ) : null}
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setPayTarget(null)}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={markPaid.isPending}
                loadingText="Saving…"
                disabled={markPaid.isPending || !payForm.formState.isValid}
              >
                <CheckCircle2 className="h-4 w-4" /> Mark paid
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
